import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccessibilityNewOutlinedIcon from '@mui/icons-material/AccessibilityNewOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useParams } from 'react-router-dom';
import AdminOrderLineItemsPanel from '../components/AdminOrderLineItemsPanel';
import SignatureCapture from '../components/order/SignatureCapture';
import CaptchaWidget from '../components/order/CaptchaWidget';
import BonusNotice from '../components/order/BonusNotice';

const NAVY = '#0d1f3c';
const HERO_DARK = '#1e2330';
const BRAND_BLUE = '#3b9eff';
const BG = '#f0f2f5';

type ReviewPayload = {
  order_reference: string;
  status: string;
  customer_type: string;
  first_name: string;
  last_name: string;
  email: string;
  service_address: string;
  billing_address?: string;
  organisation_name?: string;
  abn_acn?: string;
  items: unknown[];
  total_monthly: number;
  total_one_time: number;
  setup_fees: number;
  equipment_total: number;
  delivery_fee: number;
  coupon_code?: string | null;
  coupon_discount_amount?: number | null;
  coupon_discount_months?: number | null;
  coupon_bonus_title?: string | null;
  coupon_bonus_description?: string | null;
  coupon_bonus_terms?: string | null;
  terms_documents: { subcategory_id: string; subcategory_name: string; label: string; has_pdf: boolean }[];
  already_approved: boolean;
  customer_accepted_at?: string | null;
};

function apiBase(): string {
  return (process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : '') || '').replace(
    /\/$/,
    '',
  );
}

async function fetchReview(token: string): Promise<ReviewPayload> {
  const base = apiBase();
  const res = await fetch(`${base}/api/orders/review/${encodeURIComponent(token)}`, { credentials: 'same-origin' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || res.statusText || 'Failed to load order');
  }
  return res.json();
}

const PREVIEW_DATA: ReviewPayload = {
  order_reference: 'ORD-PRV00001',
  status: 'pending',
  customer_type: 'business',
  organisation_name: 'Acme Pty Ltd',
  abn_acn: '12 345 678 901',
  first_name: 'Jane',
  last_name: 'Smith',
  email: 'jane.smith@example.com',
  service_address: '42 Harbour View Drive, Sydney NSW 2000',
  total_monthly: 95.00,
  total_one_time: 99.00,
  setup_fees: 99.00,
  equipment_total: 0,
  delivery_fee: 0,
  already_approved: false,
  customer_accepted_at: null,
  items: [
    {
      product_id: 'preview-1',
      product_name: 'Sonic Mobile GO 40',
      product_type: 'internet_plan',
      service_type_code: 'mobile',
      cart_line_index: 1,
      instance_index: 1,
      unit_price: 95.00,
      total_price: 95.00,
      configuration: {
        name: 'Sonic Mobile GO 40',
        description: 'No connection fee',
        monthly_price: '95',
        contract_terms: '1 Month',
        number_type: 'port_in',
        porting_mobile_number: '0412 345 678',
        porting_current_provider: 'Telstra',
        porting_current_account_number: 'ACC-123456',
      },
    },
    {
      product_id: 'preview-2',
      product_name: 'Sonic Mobile GO 40',
      product_type: 'internet_plan',
      service_type_code: 'mobile',
      cart_line_index: 1,
      instance_index: 2,
      unit_price: 95.00,
      total_price: 95.00,
      configuration: {
        name: 'Sonic Mobile GO 40',
        description: 'No connection fee',
        monthly_price: '95',
        contract_terms: '1 Month',
        number_type: 'new_number',
      },
    },
  ],
  terms_documents: [],
};

async function postApprove(
  token: string,
  accept_terms: boolean,
  turnstile_token: string,
  customer_signature: string | null,
): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}/api/orders/review/${encodeURIComponent(token)}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ accept_terms, turnstile_token, customer_signature }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || res.statusText || 'Approval failed');
  }
}

function SectionCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fafbfc',
        }}
      >
        <Box sx={{ color: NAVY, display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={700} color={NAVY}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 3 }}>{children}</Box>
    </Paper>
  );
}

function TopNav() {
  return (
    <Box
      component="header"
      sx={{
        bgcolor: HERO_DARK,
        borderBottom: '1px solid rgba(255,255,255,0.10)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        py: 1.5,
        px: { xs: 2, sm: 4 },
      }}
    >
      <Box maxWidth="lg" mx="auto" display="flex" alignItems="center" justifyContent="space-between">
        <a href="https://sonicnet.net.au/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/assets/sonic-logo-white.png" alt="Sonic Internet & Voice" style={{ height: 34, display: 'block' }} />
        </a>
        <Box
          component="a"
          href="tel:13003737333"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'rgba(255,255,255,0.9)',
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 600,
            '&:hover': { color: '#fff' },
          }}
        >
          <PhoneIcon sx={{ fontSize: '1rem' }} />
          1300 37 37 33
        </Box>
      </Box>
    </Box>
  );
}

function HeroBanner({ data, approved }: { data: ReviewPayload; approved: boolean }) {
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${HERO_DARK} 60%, #162035 100%)`,
        color: '#fff',
        py: { xs: 5, sm: 7 },
        px: { xs: 2, sm: 4 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 20% 50%, rgba(59,158,255,0.12) 0%, transparent 55%), radial-gradient(circle at 80% 20%, rgba(59,158,255,0.08) 0%, transparent 45%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 700, mx: 'auto' }}>
        {approved ? (
          <CheckCircleIcon sx={{ fontSize: 56, color: '#4ade80', mb: 2 }} />
        ) : (
          <TaskAltIcon sx={{ fontSize: 48, color: BRAND_BLUE, mb: 2, opacity: 0.85 }} />
        )}

        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ color: '#fff', lineHeight: 1.25, mb: 1.5, fontSize: { xs: '1.5rem', sm: '1.9rem' } }}
        >
          {approved
            ? 'Your order has been approved!'
            : 'Thank you for placing an order with Sonic Internet & Voice'}
        </Typography>

        {!approved && (
          <Typography
            variant="body1"
            sx={{ color: 'rgba(255,255,255,0.75)', mb: 3, maxWidth: 540, mx: 'auto' }}
          >
            Please review your order details below and accept the terms and conditions to confirm your service.
          </Typography>
        )}

        {approved && data.customer_accepted_at && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.70)', mb: 3 }}>
            Approved on {new Date(data.customer_accepted_at).toLocaleString('en-AU', { dateStyle: 'long', timeStyle: 'short' })}
          </Typography>
        )}

        <Chip
          label={`Order reference: ${data.order_reference}`}
          sx={{
            bgcolor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.85rem',
            border: '1px solid rgba(255,255,255,0.22)',
            height: 34,
            letterSpacing: 0.3,
          }}
        />
      </Box>
    </Box>
  );
}

function WhatHappensNext() {
  const steps = [
    { icon: '✅', title: 'Order approved', desc: 'Your acceptance has been recorded.' },
    { icon: '📋', title: 'Order processing', desc: 'Our team reviews your order and prepares your service.' },
    { icon: '🚀', title: 'Service activation', desc: 'Your service is activated and you will receive a confirmation email.' },
  ];
  return (
    <SectionCard icon={<TaskAltIcon />} title="What happens next?">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {steps.map((s, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#f0f2f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                flexShrink: 0,
              }}
            >
              {s.icon}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700} color={NAVY}>{s.title}</Typography>
              <Typography variant="body2" color="text.secondary">{s.desc}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
}

const LEGAL_TEXT_SX = { variant: 'body2' as const, color: 'text.secondary', sx: { lineHeight: 1.75, mb: 0 } };

function LegalParagraph({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

function LegalSection({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8f9fb', border: '1px solid', borderColor: 'divider' }}>
      {children}
    </Box>
  );
}

function ApplicationDeclarationSection() {
  return (
    <SectionCard icon={<AssignmentOutlinedIcon />} title="Application Declaration">
      <LegalSection>
        <LegalParagraph>
          The Customer ("You") applies to Sonic Internet &amp; Voice Pty Ltd ABN 61 688 496 924 ("we", "us" or "our")
          for the supply of the Service(s) listed in the table above. By submitting this Application, you acknowledge
          and agree that:
        </LegalParagraph>
        <LegalParagraph>
          The Service(s) are subject to our Standard Form of Agreement, which is a standard form of agreement for the
          purposes of Part 23 of the Telecommunications Act 1997 (Cth). By submitting this Application, you confirm
          that you have been given the opportunity to read, or have read, and agree to be bound by the Standard Form of
          Agreement (including the applicable Service Description and Critical Information Summary (CIS)), available at{' '}
          <a href="https://sonicnet.net.au/legal" target="_blank" rel="noreferrer">sonicnet.net.au/legal</a>.
        </LegalParagraph>
        <LegalParagraph>
          This Application is accepted by us at the earlier of: (a) the date the Service is activated; or (b) the date
          we notify you that your Application has been accepted.
        </LegalParagraph>
        <LegalParagraph>
          If you agree to a Minimum Term for a Service, early termination during that Minimum Term may incur a
          Cancellation Charge, calculated as the monthly recurring charge for the terminated Service multiplied by the
          number of months (or part thereof) remaining in the Minimum Term, unless otherwise stated in the applicable
          CIS.
        </LegalParagraph>
        <LegalParagraph>
          The person submitting this Application warrants that they have full legal capacity to enter into this
          Agreement, or are duly authorised to execute this Application on behalf of the Customer.
        </LegalParagraph>
        <LegalParagraph>
          Any cooling-off rights available to you under the Australian Consumer Law or other applicable legislation are
          not affected by this Agreement.
        </LegalParagraph>
      </LegalSection>
    </SectionCard>
  );
}

function PrivacySpamSection({
  optOut,
  onOptOutChange,
}: {
  optOut: boolean;
  onOptOutChange: (v: boolean) => void;
}) {
  return (
    <SectionCard icon={<SecurityOutlinedIcon />} title="Privacy &amp; Spam">
      <LegalSection>
        <LegalParagraph>
          We will collect personal information about you or information about your company, including but not limited to
          your electronic contact details such as email address and telephone numbers for the purpose of administering
          the services for which you apply, and as set out in our privacy policy. Please contact us directly to obtain a
          copy of our privacy policy.
        </LegalParagraph>
        <FormControlLabel
          control={
            <Checkbox
              checked={optOut}
              onChange={(e) => onOptOutChange(e.target.checked)}
              size="small"
              sx={{ color: BRAND_BLUE, '&.Mui-checked': { color: BRAND_BLUE } }}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              If you would prefer that we do not use your personal information and/or business information for direct
              marketing and/or for sending commercial electronic messages, please check this box.
            </Typography>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
      </LegalSection>
    </SectionCard>
  );
}

const CREDIT_CHECK_POINTS = [
  'Understand that the Privacy Act 1988 (Cth) allows us to give a credit reporting agency certain personal information about you;',
  'The information we disclose to a credit reporting agency includes information which will allow you to be identified, the fact that you have applied for credit and the amount, that we are a current credit provider to you, repayments that are more than 60 days overdue and for which debt collection action has started, information that in our opinion you have acted fraudulently or shown an intention not to comply with your credit obligations, and cheques drawn by you for $100 or more which have been dishonoured more than once;',
  'Agree to our obtaining from a credit reporting agency a credit report containing information about your personal credit worthiness for purposes of assessing your application and assisting in collecting overdue payments, and to our obtaining information about your commercial activities or commercial credit worthiness from any business which provides information about the commercial credit worthiness of persons, your accountant or any other supplier to you;',
  'Agree to our giving to and obtaining from any credit provider named in your application or in a credit report on you issued by a credit reporting agency, information about your credit arrangements for the purpose of assessing your application, notifying a default by you, allowing another credit provider to ascertain the status of your arrangements with us where you are in default with one or more other credit providers and generally assessing your credit worthiness; and',
  'Understand the information exchanged can include any information about your business, personal and/or commercial credit worthiness, credit history or credit capacity which the Privacy Act allows credit providers to give to or receive from each other.',
];

function CreditChecksSection() {
  return (
    <SectionCard icon={<AccountBalanceOutlinedIcon />} title="Credit Checks">
      <LegalSection>
        <LegalParagraph>
          Prior to accepting your application, you have provided to us all information relevant to our assessment of
          your credit rating. You consent and acknowledge that you:
        </LegalParagraph>
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0 }}>
          {CREDIT_CHECK_POINTS.map((point, i) => (
            <Typography
              key={i}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.75, mb: i < CREDIT_CHECK_POINTS.length - 1 ? 1 : 0 }}
            >
              {point}
            </Typography>
          ))}
        </Box>
      </LegalSection>
    </SectionCard>
  );
}

function NationalRelaySection() {
  return (
    <SectionCard icon={<AccessibilityNewOutlinedIcon />} title="National Relay &amp; Interpreting Service">
      <LegalSection>
        <LegalParagraph>
          If you have a disability or do not have English as your first language, the Australian Government provides
          services to assist in understanding &amp; communicating with us.
        </LegalParagraph>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
              National Relay Service
            </Typography>
            <Typography variant="body2" fontWeight={700} color={NAVY}>
              <Box component="a" href="tel:133677" sx={{ color: NAVY, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                133 677
              </Box>
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Translating &amp; Interpreting Service
            </Typography>
            <Typography variant="body2" fontWeight={700} color={NAVY}>
              <Box component="a" href="tel:131450" sx={{ color: NAVY, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                131 450
              </Box>
            </Typography>
          </Box>
        </Box>
      </LegalSection>
    </SectionCard>
  );
}

const AUTHORISATION_ITEMS = [
  'All prices are quoted inclusive of GST.',
  'Full pricing can be viewed on the Critical Information Summaries for the specific plan.',
  'Mobile Broadband plans are data only plans, with no capability to receive and send calls (including emergency calls), SMS and MMS.',
  'Excluded (non-standard) call types are: Call forwarding, directory services, international voice including calls to fixed lines or mobiles that switch/divert or recalling, international roaming, Premium Calls, Premium SMS, VoIP services and 19xx services.',
  'When the included call value amount on a plan is reached all calls will be charged at the standard rates.',
  'Fair Usage policy applies to all mobile plans in relation to call usage, SMS, MMS, and data.',
  'Calls are charged in 60 second increments.',
  'A set amount of mobile data is included each month as part of the mobile plan. Unused monthly data allowance cannot be rolled over.',
  'Upon exceeding the monthly included data allowance, you will be charged excess data as per standard rates.',
  'Data usage will be metered in kilobytes, where 1024 bytes = 1 Kilobyte (KB) and includes both uploads and downloads.',
  'Sonic Mobile uses the Optus Plus 4G and 3G mobile network.',
  'The mobile product uses the Optus Plus 4G and 3G mobile network.',
  'Handsets not capable of 4G will only receive 3G coverage.',
  <>Check your area coverage at <Box component="a" href="https://www.getsonic.au/mobile/coverage" target="_blank" rel="noopener noreferrer" sx={{ color: BRAND_BLUE }}>https://www.getsonic.au/mobile/coverage</Box>.</>,
  'An Early Termination Fee (ETF) of $55 (inc GST) applies if you port your number away or cancel your service. The total amount owing will be the cost of any usage incurred up until the time of cancellation plus the minimum monthly charge and the respective ETF.',
  'Standard Form of Agreement – This Mobile Service is supplied in accordance with the terms of the Standard Form of Agreement.',
];

function AuthorisationSection() {
  return (
    <SectionCard icon={<ListAltOutlinedIcon />} title="Authorisation">
      <LegalSection>
        <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 0 }}>
          {AUTHORISATION_ITEMS.map((item, i) => (
            <Typography
              key={i}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.75, mb: i < AUTHORISATION_ITEMS.length - 1 ? 0.75 : 0 }}
            >
              {item}
            </Typography>
          ))}
        </Box>
      </LegalSection>
    </SectionCard>
  );
}

const MNP_LEGAL_TEXT = [
  'Porting hours: 8am to 8pm Mon–Fri and 10am to 6pm on Sat (AEDST/AEST), except national public holidays. Porting must take place within 30 days of the authorisation date on this form.',
  'You authorise: The above mobile service numbers to be ported to us. The mobile service numbers, the identity of your new service provider (us) and network type to be disclosed to other network providers and portability service suppliers for the purpose of complaint handling, network fault management, and the routing of calls and SMS messages to your mobile service number after porting activity has taken place. Our representative (acting in good faith) may complete and sign a new MNP Customer Authorisation for the purposes of carrying out the port to us, in circumstances where this MNP Authorisation expires, additional details are to be added, editing, or deleting details are required. Our representative (acting in good faith) to complete any blank spaces, missing or incomplete information on this MNP Customer Authorisation on your behalf.',
  'You acknowledge and agree that: You are authorised to request the porting of the mobile service numbers listed on this form. You have been advised that by porting the mobile service numbers listed on this form, the service and/or related services associated with that number may or may not be disconnected from the existing mobile service provider. Authorisation Date is the date you signed this MNP Customer Authorisation. This MNP Customer Authorisation is valid for 30 days from the Authorisation Date. There may be costs and obligations associated with your existing mobile service and with porting your mobile service numbers. You may or may not have an existing contract with your existing mobile service provider; and your current contract may or may not include an obligation to make an early termination payment to your existing service provider.',
];

function MobilePortabilitySection({ items }: { items: unknown[] }) {
  type MobileItem = {
    cart_line_index?: number;
    instance_index?: number;
    product_name?: string;
    configuration?: Record<string, unknown>;
  };

  const mobileItems = items.filter((it): it is MobileItem => {
    const i = it as any;
    return i != null && typeof i === 'object' && i.service_type_code === 'mobile';
  });

  if (mobileItems.length === 0) return null;

  const portInItems = mobileItems.filter((it) => {
    const numType = String(it.configuration?.number_type ?? '');
    return numType === 'port_in';
  });

  return (
    <SectionCard icon={<PhoneAndroidOutlinedIcon />} title="Mobile Number Portability">
      {/* Porting details table */}
      {mobileItems.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following mobile services are included in this order:
          </Typography>
          <Table size="small" sx={{ mb: 0 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: NAVY }}>
                <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Number type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Mobile number</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Provider</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Account #</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mobileItems.map((it, idx) => {
                const cfg = it.configuration || {};
                const numType = String(cfg.number_type ?? 'new_number');
                const isPortIn = numType === 'port_in';
                return (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color={NAVY}>
                        {it.product_name || 'Mobile service'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {isPortIn ? 'Port in' : 'New number'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{isPortIn ? String(cfg.porting_mobile_number ?? '—') : '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{isPortIn ? String(cfg.porting_current_provider ?? '—') : '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{isPortIn ? String(cfg.porting_current_account_number ?? '—') : '—'}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Legal text — only shown if there are port-in items */}
      {portInItems.length > 0 && (
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: '#f8f9fb',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 1.5 }}>
            MNP Customer Authorisation
          </Typography>
          {MNP_LEGAL_TEXT.map((para, i) => (
            <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: i < MNP_LEGAL_TEXT.length - 1 ? 1.5 : 0, lineHeight: 1.65 }}>
              {para}
            </Typography>
          ))}
        </Box>
      )}
    </SectionCard>
  );
}

const VOICE_PORT_AUTHORITY_AGENCY_TEXT = [
  'I authorise Sonic Internet & Voice and/or its nominated agents to complete and sign a new Port Authority Form (PAF) for the purposes of carrying out the port to Sonic Internet & Voice in circumstances where: the PAF expires; additional details are to be added; editing or deleting of details is required; or a third party is required to perform part or all of the port.',
  'This authority will remain in place for 12 months from the date of signature or until such time as Sonic Internet & Voice is otherwise notified.',
];

const VOICE_LNP_ITEMS = [
  'The Porting of your Phone Number will be conducted in accordance with the "Australian Communications Industry Forum, Industry Code — Local Number Portability ACIF C540:2013 Dec 2013" ("LNP Code") and any bilateral arrangements. Subject to the terms and conditions of the Sonic Internet & Voice Standard Form of Agreement, you may Port your Phone Number from your previous Supplier to Sonic Internet & Voice if that Phone Number is declared Portable under the ACA Numbering Plan 1997 and no exemption has been granted by the ACA.',
  'If you wish to Port your Phone Number from Sonic Internet & Voice to another Supplier, you must contact that other Supplier. Porting from Sonic Internet & Voice to another Supplier will be conducted in accordance with the LNP Code and any other bilateral arrangements.',
  'In order for Sonic Internet & Voice to Port your Phone Number you must complete and sign this Porting Authority Form (PAF), which also includes authority for an Emergency Return. In order for the Port to proceed this form must be completed in accordance with the LNP Code and any other bilateral arrangements.',
  'In accordance with the LNP Code, a request for Porting shall be rejected if: (a) the request is for the Porting of Out of Area Numbers; (b) the request is for the Porting of non-portable telephone numbers; (c) the LNP Code requires the request to be rejected; or (d) Sonic Internet & Voice cannot otherwise provide portability for that Phone Number in that circumstance.',
  'Sonic Internet & Voice is able to provide you with the facility to implement the Porting of your telephone number(s) outside of your normal business hours of operation, if required. Sonic Internet & Voice will require access to your premises for the porting implementation. Failure to provide access to your premises at agreed times or failure to provide sufficient notice to change an agreed time may result in: (a) the Porting implementation not being completed within the agreed implementation schedule; and (b) loss of existing Service.',
  'Sonic Internet & Voice cannot Port your Phone Number and move the address of your Phone Number on the same day.',
  'If your Phone Number(s) is inactive at the time of the Porting by Sonic Internet & Voice, you must notify Sonic Internet & Voice as soon as the Phone number(s) become active.',
  'Porting typically results in a service outage of approximately 20 minutes, but the duration of the outage can vary. In the event of a port, withdrawal or reversal, Sonic Internet & Voice is not responsible for any period of outage.',
  'Sonic Internet & Voice charges you for the Porting of a Phone Number. For charges please refer to the Voice Service Schedule.',
  "You acknowledge that by Porting the Telephone Number, any DSL/Spectrum Sharing Service associated with that Telephone Number is disconnected and may result in finalisation of the Customer's DSL/Spectrum Sharing account for that service.",
  'You acknowledge that although you have the right to Port your Telephone Number(s) to Sonic Internet & Voice, there may be costs and obligations associated with the Port which may include early termination fees and Porting fees for your existing carrier.',
];

const VOICE_EMERGENCY_ITEMS = [
  'Prior to signing the Porting Authority Form, you must negotiate and agree with your previous Supplier regarding the service that could be provided in the event that an Emergency Return is required.',
  'If you have Ported your Phone Number away from Sonic Internet & Voice, the terms and conditions set out herein and in the Sonic Internet & Voice Standard Form of Agreement will apply to your Service in the event that an Emergency Return to Sonic Internet & Voice is required.',
  'In the event of an Emergency Return to your previous Supplier, you may experience an extended period of outage whilst the Service from your Supplier is restored.',
  'Sonic Internet & Voice will endeavour to assist you in the event of an Emergency Return to Sonic Internet & Voice, in which you may experience an extended period of outage whilst the telephone numbers are being restored.',
  'Acting in accordance with the LNP Code and any other bilateral arrangements, in the event of an Emergency Return to your previous Supplier, Sonic Internet & Voice: (a) will notify your previous Supplier of the Emergency Return requirement; (b) is not responsible for any period of outage; and (c) is not liable to you or any person claiming through you for any damage, loss, costs or expenses or other liability in contract, tort or otherwise direct or indirect, for or in relation to the Emergency Return (including the negligent act or omission of Sonic Internet & Voice).',
  'In the event of an Emergency Return to Sonic Internet & Voice, Sonic Internet & Voice: (a) is not responsible for any period of outage; and (b) is not liable to you or any person claiming through you for damage, loss, costs or expenses or other liability in contract, tort or otherwise direct or indirect, for or in relation to the Emergency Return (including the negligent act or omission of Sonic Internet & Voice).',
];

function VoicePortAuthoritySection({ items }: { items: unknown[] }) {
  type VoiceItem = {
    product_name?: string;
    configuration?: Record<string, unknown>;
  };

  const voicePortInItems = items.filter((it): it is VoiceItem => {
    const i = it as any;
    if (i == null || typeof i !== 'object' || i.service_type_code !== 'voice') return false;
    const cfg = i.configuration || {};
    // Explicit port_in flag, or fallback: porting_document_path present means port-in was selected
    return String(cfg.number_type ?? '') === 'port_in' || !!cfg.porting_document_path;
  });

  if (voicePortInItems.length === 0) return null;

  return (
    <SectionCard icon={<LocalPhoneOutlinedIcon />} title="Port Authority">
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The following fixed number(s) are being ported as part of this order:
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NAVY }}>
              <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Service</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Number(s) to port</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Current provider</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#fff', borderBottom: 'none' }}>Account #</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {voicePortInItems.map((it, idx) => {
              const cfg = it.configuration || {};
              return (
                <TableRow key={idx}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color={NAVY}>
                      {it.product_name || 'Voice service'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {String(cfg.porting_fixed_numbers ?? '—')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{String(cfg.porting_current_provider ?? '—')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{String(cfg.porting_current_account_number ?? '—')}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <LegalSection>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Terms &amp; Conditions
          </Typography>

          <Typography variant="body2" fontWeight={700} color={NAVY} sx={{ mb: 1 }}>
            1. Local Number Portability
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 2.5 }}>
            {VOICE_LNP_ITEMS.map((item, i) => (
              <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: i < VOICE_LNP_ITEMS.length - 1 ? 0.75 : 0 }}>
                {item}
              </Typography>
            ))}
          </Box>

          <Typography variant="body2" fontWeight={700} color={NAVY} sx={{ mb: 1 }}>
            2. Emergency Return
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 0 }}>
            {VOICE_EMERGENCY_ITEMS.map((item, i) => (
              <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: i < VOICE_EMERGENCY_ITEMS.length - 1 ? 0.75 : 0 }}>
                {item}
              </Typography>
            ))}
          </Box>
        </LegalSection>
      </Box>

      <LegalSection>
        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 1.5 }}>
          Agency Agreement
        </Typography>
        {VOICE_PORT_AUTHORITY_AGENCY_TEXT.map((para, i) => (
          <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: i < VOICE_PORT_AUTHORITY_AGENCY_TEXT.length - 1 ? 1.25 : 0, lineHeight: 1.65 }}>
            {para}
          </Typography>
        ))}
      </LegalSection>
    </SectionCard>
  );
}

function OrderReviewPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ReviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pdfModal, setPdfModal] = useState<{ label: string; src: string; downloadSrc: string } | null>(null);
  const [directMarketingOptOut, setDirectMarketingOptOut] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileKeyRef = useRef(0);

  useEffect(() => {
    if (!token) {
      setError('This link is invalid or missing.');
      setLoading(false);
      return;
    }
    if (token === 'preview') {
      setData(PREVIEW_DATA);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchReview(token);
        if (!cancelled) setData(d);
      } catch (e: unknown) {
        if (!cancelled) setError((e as Error)?.message || 'Failed to load order');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleApprove = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      if (token === 'preview') {
        await new Promise((r) => setTimeout(r, 800));
        setData({ ...PREVIEW_DATA, already_approved: true, customer_accepted_at: new Date().toISOString() });
        setDone(true);
      } else {
        await postApprove(token, accepted, turnstileToken || '', signature);
        setDone(true);
        const d = await fetchReview(token);
        setData(d);
      }
    } catch (e: unknown) {
      setError((e as Error)?.message || 'Could not submit approval');
      // Force Turnstile to re-render so the used token can't be resubmitted
      setTurnstileToken(null);
      turnstileKeyRef.current += 1;
    } finally {
      setSubmitting(false);
    }
  };

  const pdfSrc = (subId: string) =>
    `${apiBase()}/api/orders/review/${encodeURIComponent(token || '')}/terms/${encodeURIComponent(subId)}`;

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: BG }}>
        <TopNav />
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
          <img src="/assets/sonic-logo.png" alt="Sonic Internet & Voice" style={{ height: 40, opacity: 0.6 }} />
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">Loading your order…</Typography>
        </Box>
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: BG }}>
        <TopNav />
        <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, px: 2 }}>
          <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
        </Box>
      </Box>
    );
  }

  if (!data) return null;

  const approved = data.already_approved || done;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, display: 'flex', flexDirection: 'column' }}>
      <TopNav />

      <HeroBanner data={data} approved={approved} />

      <Box sx={{ maxWidth: 1100, width: '100%', mx: 'auto', px: { xs: 2, sm: 3 }, py: 4, flex: 1 }}>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {approved && <WhatHappensNext />}

        {/* Your details */}
        <SectionCard icon={<PersonOutlineIcon />} title="Your details">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Business banner — shown above all other fields for business customers */}
            {data.customer_type === 'business' && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  p: 2.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${NAVY} 0%, #1a3560 100%)`,
                  color: '#fff',
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)' }}>
                    Business name
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.3 }}>
                    {data.organisation_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)' }}>
                    ABN / ACN
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.3 }}>
                    {data.abn_acn}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Common fields */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem' }}>
                  Full name
                </Typography>
                <Typography variant="body1" fontWeight={600} color={NAVY}>
                  {data.first_name} {data.last_name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem' }}>
                  Email address
                </Typography>
                <Typography variant="body1" fontWeight={600} color={NAVY}>
                  {data.email}
                </Typography>
              </Box>
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <HomeOutlinedIcon sx={{ fontSize: '0.85rem' }} /> Service address
                </Typography>
                <Typography variant="body1" fontWeight={600} color={NAVY}>
                  {data.service_address}
                </Typography>
              </Box>
            </Box>
          </Box>
        </SectionCard>

        {/* Order lines */}
        <SectionCard icon={<ShoppingCartOutlinedIcon />} title="Order lines">
          <AdminOrderLineItemsPanel orderItems={{ items: data.items }} />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" fontWeight={700} color={NAVY} gutterBottom>
            Order summary
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            {/* Monthly */}
            <Box
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${NAVY} 0%, #1a3560 100%)`,
                color: '#fff',
                p: 3,
              }}
            >
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}>
                Monthly recurring
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: '#fff', lineHeight: 1 }}>
                ${Number(data.total_monthly || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>per month (incl. GST)</Typography>
            </Box>

            {/* One-time */}
            <Box
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                p: 3,
              }}
            >
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontSize: '0.7rem' }}>
                One-time charges
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: NAVY, lineHeight: 1 }}>
                ${Number(data.total_one_time || 0).toFixed(2)}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              {[
                { label: 'Setup fees', value: data.setup_fees ?? 0 },
                { label: 'Equipment', value: data.equipment_total ?? 0 },
                { label: 'Delivery', value: data.delivery_fee ?? 0 },
              ].map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="caption" fontWeight={600}>${Number(value).toFixed(2)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </SectionCard>

        {/* Mobile Number Portability */}
        <MobilePortabilitySection items={data.items} />

        {/* Application Declaration */}
        <ApplicationDeclarationSection />

        {/* Voice Port Authority */}
        <VoicePortAuthoritySection items={data.items} />

        {/* Privacy & Spam */}
        <PrivacySpamSection optOut={directMarketingOptOut} onOptOutChange={setDirectMarketingOptOut} />

        {/* Credit Checks */}
        <CreditChecksSection />

        {/* National Relay & Interpreting Service */}
        <NationalRelaySection />

        {/* Bonus from promo code, with its own terms */}
        {data.coupon_bonus_title && (
          <SectionCard icon={<GavelOutlinedIcon />} title="Bonus offer">
            <BonusNotice
              title={data.coupon_bonus_title}
              description={data.coupon_bonus_description}
              terms={data.coupon_bonus_terms}
            />
          </SectionCard>
        )}

        {/* Terms & conditions */}
        {data.terms_documents && data.terms_documents.length > 0 && (
          <SectionCard icon={<GavelOutlinedIcon />} title="Terms & conditions">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please read each document carefully before approving your order. Click a document to preview it.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {data.terms_documents.map((t) => (
                <Paper
                  key={t.subcategory_id}
                  variant="outlined"
                  onClick={() => setPdfModal({
                    label: t.label || t.subcategory_name,
                    src: pdfSrc(t.subcategory_id),
                    downloadSrc: `${pdfSrc(t.subcategory_id)}?download=1`,
                  })}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: BRAND_BLUE,
                      bgcolor: 'rgba(59,158,255,0.04)',
                      '& .doc-arrow': { opacity: 1 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(13,31,60,0.07)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ color: NAVY, fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} color={NAVY} noWrap>
                      {t.label || t.subcategory_name}
                    </Typography>
                    {t.label && t.subcategory_name !== t.label && (
                      <Typography variant="caption" color="text.secondary">{t.subcategory_name}</Typography>
                    )}
                  </Box>
                  <OpenInNewIcon className="doc-arrow" sx={{ color: BRAND_BLUE, opacity: 0.4, fontSize: 18, transition: 'opacity 0.15s ease' }} />
                </Paper>
              ))}
            </Box>
          </SectionCard>
        )}

        {/* Standard Form of Agreement */}
        <SectionCard icon={<DescriptionOutlinedIcon />} title="Standard Form of Agreement">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your mobile service is supplied in accordance with our Standard Form of Agreement. Please read this document before approving your order.
          </Typography>
          <Paper
            variant="outlined"
            onClick={() => setPdfModal({
              label: 'Standard Form of Agreement',
              src: '/assets/Standard_Form_of_Agreement.pdf',
              downloadSrc: '/assets/Standard_Form_of_Agreement.pdf',
            })}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                borderColor: BRAND_BLUE,
                bgcolor: 'rgba(59,158,255,0.04)',
                '& .doc-arrow': { opacity: 1 },
              },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                bgcolor: 'rgba(13,31,60,0.07)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: NAVY, fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} color={NAVY}>
                Standard Form of Agreement — Sonic Internet &amp; Voice
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Telecommunications Act 1997 (Cth) — Part 23
              </Typography>
            </Box>
            <OpenInNewIcon className="doc-arrow" sx={{ color: BRAND_BLUE, opacity: 0.4, fontSize: 18, transition: 'opacity 0.15s ease' }} />
          </Paper>
        </SectionCard>

        {/* PDF preview modal */}
        <Dialog
          open={!!pdfModal}
          onClose={() => setPdfModal(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: NAVY,
              color: '#fff',
              py: 1.5,
              px: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, flexShrink: 0 }} />
              <Typography variant="subtitle1" fontWeight={700} noWrap>{pdfModal?.label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Button
                component="a"
                href={pdfModal?.downloadSrc}
                download
                size="small"
                startIcon={<DownloadIcon />}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.4)',
                  border: '1px solid',
                  borderRadius: 1.5,
                  px: 1.5,
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Download
              </Button>
              <IconButton onClick={() => setPdfModal(null)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
            {pdfModal && (
              <Box
                component="iframe"
                src={pdfModal.src}
                title={pdfModal.label}
                sx={{ flex: 1, width: '100%', border: 'none', display: 'block' }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Approval */}
        {!approved && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${NAVY} 0%, #1a3560 100%)`,
                px: 3,
                py: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <TaskAltIcon sx={{ color: BRAND_BLUE }} />
              <Typography variant="subtitle1" fontWeight={700} color="#fff">
                Approve your order
              </Typography>
            </Box>

            <Box sx={{ p: 3, bgcolor: '#fff' }}>
              {(!data.terms_documents || data.terms_documents.length === 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No PDF terms are attached to this order. By approving, you confirm the order summary above is correct.
                </Typography>
              )}

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: accepted ? BRAND_BLUE : 'divider',
                  borderWidth: accepted ? 2 : 1,
                  bgcolor: accepted ? 'rgba(59,158,255,0.04)' : 'transparent',
                  transition: 'all 0.2s ease',
                  mb: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={accepted}
                      onChange={(e) => setAccepted(e.target.checked)}
                      sx={{ color: BRAND_BLUE, '&.Mui-checked': { color: BRAND_BLUE } }}
                    />
                  }
                  label={
                    <Typography variant="body2" color={NAVY} sx={{ lineHeight: 1.6 }}>
                      {data.terms_documents && data.terms_documents.length > 0
                        ? 'I have read and accept all terms and conditions shown above, including any linked documents. I authorise Sonic Internet & Voice to proceed with this order.'
                        : 'I confirm the order details are correct and authorise Sonic Internet & Voice to proceed with this order on behalf of the account holder.'}
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
              </Paper>

              <Box sx={{ mb: 3 }}>
                <SignatureCapture
                  key={turnstileKeyRef.current}
                  onChange={setSignature}
                  disabled={submitting}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <CaptchaWidget
                  key={turnstileKeyRef.current}
                  siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ''}
                  onVerify={(t) => setTurnstileToken(t)}
                  onError={() => setTurnstileToken(null)}
                />
              </Box>

              <Button
                variant="contained"
                size="large"
                disabled={!accepted || !signature || !turnstileToken || submitting}
                onClick={() => void handleApprove()}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                sx={{
                  bgcolor: (accepted && signature && turnstileToken) ? NAVY : undefined,
                  '&:hover': { bgcolor: (accepted && signature && turnstileToken) ? '#162035' : undefined },
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  boxShadow: (accepted && signature && turnstileToken) ? '0 4px 14px rgba(13,31,60,0.3)' : undefined,
                  transition: 'all 0.2s ease',
                }}
              >
                {submitting ? 'Submitting…' : 'Approve order'}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: HERO_DARK,
          color: 'rgba(255,255,255,0.60)',
          py: 3,
          px: { xs: 2, sm: 4 },
          mt: 4,
          textAlign: 'center',
        }}
      >
        <img src="/assets/sonic-logo-white.png" alt="Sonic Internet & Voice" style={{ height: 28, opacity: 0.7, marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
          Need help? Call us on{' '}
          <Box component="a" href="tel:13003737333" sx={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontWeight: 600 }}>
            1300 37 37 33
          </Box>
        </Typography>
        <Typography variant="caption" display="block">
          © {new Date().getFullYear()} Sonic Internet & Voice. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}

export default OrderReviewPage;
